import React, { useState, useEffect, useCallback } from 'react'
import api from '../lib/api'
import { useUserTimezone, zonedDateTimeToUtc } from '@/hooks/useUserTimezone'
import { getUserTimezone } from '../lib/timezone'
import { formatPhoneDisplay } from '@/lib/utils'
import { onSocketEvent } from '../hooks/useWebSocket'
import { useToast } from '@/hooks/use-toast'
import { formatDistanceToNow, format } from 'date-fns'
import {
  PhoneForwarded,
  MoreHorizontal,
  Calendar,
  PhoneCall,
  XCircle,
  Clock,
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  Search,
  Loader2
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { MobileListItem } from '@/components/ui/MobileListItem'
import { PageHeader, StatCard, EmptyState } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog'

interface QueueEntry {
  id: string
  phoneNumber: string
  status: string
  priority: number
  attempts: number
  maxAttempts: number
  queuedAt: string
  nextAttemptAt: string | null
  lastAttemptAt: string | null
  expiresAt: string | null
  callerName: string | null
  aiSummary: string | null
  callbackReason: string | null
  notes: string | null
  voiceCallId: string | null
  sentiment: 'positive' | 'neutral' | 'negative' | null
  qualificationScore: number | null
}

interface QueueSummary {
  queued: number
  processing: number
  completed: number
  failed: number
  cancelled: number
  blocked_dnc: number
}

export default function FollowUpQueuePage() {
  const { toast } = useToast()
  const userTz = useUserTimezone()
  const [entries, setEntries] = useState<QueueEntry[]>([])
  const [summary, setSummary] = useState<QueueSummary>({ queued: 0, processing: 0, completed: 0, failed: 0, cancelled: 0, blocked_dnc: 0 })
  const [scheduledToday, setScheduledToday] = useState(0)
  const [completedToday, setCompletedToday] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('active')
  const [offset, setOffset] = useState(0)
  const limit = 50

  // Reschedule dialog state
  const [rescheduleEntry, setRescheduleEntry] = useState<QueueEntry | null>(null)
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [rescheduleTime, setRescheduleTime] = useState('')
  const [isRescheduling, setIsRescheduling] = useState(false)

  // Notes dialog state
  const [notesEntry, setNotesEntry] = useState<QueueEntry | null>(null)
  const [notesValue, setNotesValue] = useState('')
  const [isSavingNotes, setIsSavingNotes] = useState(false)

  // Action loading states
  const [callingNowId, setCallingNowId] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [orgTimezone, setOrgTimezone] = useState<string>(getUserTimezone())

  const loadingRef = React.useRef(false)
  const loadQueue = useCallback(async () => {
    if (loadingRef.current) return
    loadingRef.current = true
    try {
      const params = new URLSearchParams()
      params.append('status', statusFilter)
      params.append('limit', limit.toString())
      params.append('offset', offset.toString())

      const response = await api.get(`/api/voice/follow-up-queue?${params.toString()}`)
      if (response.data) {
        const data = response.data
        setEntries(data.entries || [])
        // Merge with defaults: API only returns keys for statuses with rows, so missing keys would otherwise be undefined and cause NaN in `queued + processing`.
        setSummary({
          queued: 0, processing: 0, completed: 0, failed: 0, cancelled: 0, blocked_dnc: 0,
          ...(data.summary || {})
        })
        setScheduledToday(data.scheduledToday || 0)
        setCompletedToday(data.completedToday || 0)
        if (data.timezone) setOrgTimezone(data.timezone)
      }
    } catch (error) {
      console.error('Failed to load follow-up queue:', error)
      toast({ title: 'Error', description: 'Failed to load follow-up queue', variant: 'destructive' })
    } finally {
      setIsLoading(false)
      loadingRef.current = false
    }
  }, [statusFilter, offset])

  useEffect(() => {
    loadQueue()
  }, [loadQueue])

  // Real-time updates via Socket.IO
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const cleanup = onSocketEvent('queue:updated', () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        loadQueue()
      }, 500)
    })
    return () => {
      cleanup()
      clearTimeout(timer)
    }
  }, [loadQueue])

  const handleCallNow = async (entry: QueueEntry) => {
    setCallingNowId(entry.id)
    try {
      await api.post(`/api/voice/follow-up-queue/${entry.id}/call-now`)
      toast({ title: 'Call initiated', description: `Calling ${entry.callerName || formatPhoneDisplay(entry.phoneNumber)} now.` })
      loadQueue()
    } catch (error: any) {
      toast({
        title: 'Failed to initiate call',
        description: error.response?.data?.message || 'Please try again.',
        variant: 'destructive'
      })
    } finally {
      setCallingNowId(null)
    }
  }

  const handleCancel = async (entry: QueueEntry) => {
    setCancellingId(entry.id)
    try {
      await api.delete(`/api/voice/follow-up-queue/${entry.id}`)
      toast({ title: 'Follow-up cancelled', description: 'The follow-up has been removed from the queue.' })
      loadQueue()
    } catch (error: any) {
      toast({
        title: 'Failed to cancel follow-up',
        description: error.response?.data?.message || 'Please try again.',
        variant: 'destructive'
      })
    } finally {
      setCancellingId(null)
    }
  }

  const openRescheduleDialog = (entry: QueueEntry) => {
    const nextDate = entry.nextAttemptAt ? new Date(entry.nextAttemptAt) : new Date()
    setRescheduleDate(format(nextDate, 'yyyy-MM-dd'))
    setRescheduleTime(format(nextDate, 'HH:mm'))
    setRescheduleEntry(entry)
  }

  const handleReschedule = async () => {
    if (!rescheduleEntry || !rescheduleDate || !rescheduleTime) return
    const scheduledDate = zonedDateTimeToUtc(rescheduleDate, rescheduleTime, userTz)
    if (scheduledDate <= new Date()) {
      toast({
        title: 'Invalid date',
        description: 'Reschedule date must be in the future.',
        variant: 'destructive'
      })
      return
    }
    setIsRescheduling(true)
    try {
      const scheduledAt = scheduledDate.toISOString()
      await api.patch(`/api/voice/follow-up-queue/${rescheduleEntry.id}`, { scheduledAt })
      toast({ title: 'Rescheduled', description: 'Follow-up has been rescheduled.' })
      setRescheduleEntry(null)
      loadQueue()
    } catch (error: any) {
      toast({
        title: 'Failed to reschedule',
        description: error.response?.data?.message || 'Please try again.',
        variant: 'destructive'
      })
    } finally {
      setIsRescheduling(false)
    }
  }

  const openNotesDialog = (entry: QueueEntry) => {
    setNotesValue(entry.notes || '')
    setNotesEntry(entry)
  }

  const handleSaveNotes = async () => {
    if (!notesEntry) return
    setIsSavingNotes(true)
    try {
      await api.patch(`/api/voice/follow-up-queue/${notesEntry.id}`, { notes: notesValue })
      toast({ title: 'Notes saved' })
      setNotesEntry(null)
      loadQueue()
    } catch (error: any) {
      toast({
        title: 'Failed to save notes',
        description: error.response?.data?.message || 'Please try again.',
        variant: 'destructive'
      })
    } finally {
      setIsSavingNotes(false)
    }
  }

  const formatNextAttempt = (nextAttemptAt: string | null): string => {
    if (!nextAttemptAt) return '--'
    const date = new Date(nextAttemptAt)
    const now = new Date()

    if (date < now) return 'Overdue'

    // Compare dates in the org's timezone to get correct Today/Tomorrow
    const dateFmt = new Intl.DateTimeFormat('en-US', { timeZone: orgTimezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    const timeFmt = new Intl.DateTimeFormat('en-US', { timeZone: orgTimezone, hour: 'numeric', minute: '2-digit', hour12: true })
    const dateStr = dateFmt.format(date)
    const nowStr = dateFmt.format(now)
    const tomorrowDate = new Date(now.getTime() + 24 * 60 * 60_000)
    const tomorrowStr = dateFmt.format(tomorrowDate)
    const timeDisplay = timeFmt.format(date)

    if (dateStr === nowStr) return `Today ${timeDisplay}`
    if (dateStr === tomorrowStr) return `Tomorrow ${timeDisplay}`

    return formatDistanceToNow(date, { addSuffix: true })
  }

  const getReasonBadge = (reason: string | null) => {
    if (!reason) return <span className="text-muted-foreground text-xs">--</span>
    const configs: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      voicemail: { variant: 'outline', label: 'Voicemail' },
      no_answer: { variant: 'secondary', label: 'No Answer' },
      incomplete: { variant: 'destructive', label: 'Incomplete' },
      outbound_voicemail: { variant: 'outline', label: 'VM (Outbound)' },
      outbound_no_answer: { variant: 'secondary', label: 'No Answer (Out)' }
    }
    const config = configs[reason] || { variant: 'outline' as const, label: reason }
    return <Badge variant={config.variant} className="text-xs">{config.label}</Badge>
  }

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string; className?: string }> = {
      queued: { variant: 'secondary', label: 'Queued' },
      processing: { variant: 'default', label: 'Processing', className: 'animate-pulse' },
      completed: { variant: 'default', label: 'Completed', className: 'bg-green-600' },
      failed: { variant: 'destructive', label: 'Failed' },
      blocked_dnc: { variant: 'destructive', label: 'DNC Blocked' },
      cancelled: { variant: 'outline', label: 'Cancelled' }
    }
    const config = configs[status] || { variant: 'outline' as const, label: status }
    return <Badge variant={config.variant} className={`text-xs ${config.className || ''}`}>{config.label}</Badge>
  }

  const getNextAttemptColor = (nextAttemptAt: string | null): string => {
    if (!nextAttemptAt) return 'text-muted-foreground'
    const date = new Date(nextAttemptAt)
    if (date < new Date()) return 'text-destructive font-medium'
    return 'text-foreground'
  }

  const filteredEntries = entries.filter(entry => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      entry.phoneNumber?.toLowerCase().includes(query) ||
      entry.callerName?.toLowerCase().includes(query)
    )
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  return (
    <div className="">
      <PageHeader
        eyebrow="Outreach"
        title="Follow-Up Queue"
        subtitle="Pending outbound follow-ups for prospects who didn't answer or went to voicemail."
        icon={
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
            <PhoneForwarded className="h-5 w-5 text-brand-royal" />
          </div>
        }
      />

      {/* Summary — bento: prominent Pending + 3 supporting stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 stagger-fade">
        <StatCard
          className="col-span-2 lg:col-span-2 lg:row-span-2"
          label="Pending"
          value={summary.queued + summary.processing}
          icon={Clock}
          tone="periwinkle"
          hint={summary.processing > 0 ? `${summary.processing} processing now` : 'Waiting in queue'}
        />
        <StatCard
          label="Scheduled today"
          value={scheduledToday}
          icon={Calendar}
          tone="royal"
          hint="Follow-ups due today"
        />
        <StatCard
          label="Needs attention"
          value={summary.failed + summary.blocked_dnc}
          icon={AlertCircle}
          tone={summary.failed + summary.blocked_dnc > 0 ? 'rose' : 'neutral'}
          hint={summary.failed + summary.blocked_dnc > 0 ? 'Requires manual review' : 'No failures'}
        />
        <StatCard
          className="col-span-2 lg:col-span-2"
          label="Completed today"
          value={completedToday}
          icon={CheckCircle2}
          tone="green"
          hint="Successfully reached"
        />
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl border bg-card shadow-sm card-inset overflow-hidden mb-5">
        <div className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or phone number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setOffset(0) }}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Queue Table */}
      <Card className="border bg-card shadow-sm card-inset overflow-hidden">
        {filteredEntries.length === 0 ? (
          <div className="p-1">
            <EmptyState
              icon={PhoneForwarded}
              tone={statusFilter !== 'active' ? 'neutral' : 'periwinkle'}
              title={statusFilter !== 'active' ? 'No matching follow-ups' : 'Queue is clear'}
              description={statusFilter !== 'active'
                ? 'Try adjusting your filters to see more results.'
                : 'Follow-ups auto-queue when callers leave voicemails or miss calls — your AI schedules retries on its own.'}
            />
          </div>
        ) : (
          <CardContent className="p-0">
            {/* Mobile card list — replaces the horizontal-scroll table on phones */}
            <div className="sm:hidden divide-y">
              {filteredEntries.map((entry) => (
                <MobileListItem
                  key={entry.id}
                  icon={PhoneForwarded}
                  title={entry.callerName || <span className="text-muted-foreground">Unknown</span>}
                  subtitle={formatPhoneDisplay(entry.phoneNumber, '--')}
                  status={getStatusBadge(entry.status)}
                  actions={
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-9 w-9">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleCallNow(entry)}
                          disabled={callingNowId === entry.id || entry.status === 'completed' || entry.status === 'cancelled'}
                        >
                          {callingNowId === entry.id ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <PhoneCall className="mr-2 h-4 w-4" />
                          )}
                          Call Now
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openRescheduleDialog(entry)}
                          disabled={entry.status === 'completed' || entry.status === 'cancelled'}
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          Reschedule
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openNotesDialog(entry)}>
                          <MessageSquare className="mr-2 h-4 w-4" />
                          {entry.notes ? 'Edit Note' : 'Add Note'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleCancel(entry)}
                          disabled={cancellingId === entry.id || entry.status === 'completed' || entry.status === 'cancelled'}
                          className="text-destructive focus:text-destructive"
                        >
                          {cancellingId === entry.id ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <XCircle className="mr-2 h-4 w-4" />
                          )}
                          Cancel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  }
                >
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {getReasonBadge(entry.callbackReason)}
                    </div>
                    <div className="flex items-center justify-between text-xs gap-3">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <span className={`truncate ${getNextAttemptColor(entry.nextAttemptAt)}`}>
                          {formatNextAttempt(entry.nextAttemptAt)}
                        </span>
                      </span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-muted-foreground">{entry.attempts}/{entry.maxAttempts}</span>
                        <div
                          className="w-12 h-1.5 bg-muted rounded-full overflow-hidden"
                          role="progressbar"
                          aria-valuenow={entry.attempts}
                          aria-valuemin={0}
                          aria-valuemax={entry.maxAttempts}
                        >
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${(entry.attempts / entry.maxAttempts) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    {entry.aiSummary && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {entry.aiSummary}
                      </p>
                    )}
                    {entry.notes && (
                      <p className="text-xs italic text-muted-foreground line-clamp-1">
                        “{entry.notes}”
                      </p>
                    )}
                  </div>
                </MobileListItem>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Prospect</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Next Attempt</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    {/* Prospect */}
                    <TableCell className="font-medium">
                      {entry.callerName || (
                        <span className="text-muted-foreground">Unknown</span>
                      )}
                    </TableCell>

                    {/* Phone */}
                    <TableCell className="text-sm">
                      {formatPhoneDisplay(entry.phoneNumber, '--')}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      {getStatusBadge(entry.status)}
                    </TableCell>

                    {/* Reason */}
                    <TableCell>
                      {getReasonBadge(entry.callbackReason)}
                    </TableCell>

                    {/* Next Attempt */}
                    <TableCell>
                      <span className={`text-sm ${getNextAttemptColor(entry.nextAttemptAt)}`}>
                        {formatNextAttempt(entry.nextAttemptAt)}
                      </span>
                    </TableCell>

                    {/* Attempts */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{entry.attempts}/{entry.maxAttempts}</span>
                        <div
                          className="w-12 h-1.5 bg-muted rounded-full overflow-hidden"
                          role="progressbar"
                          aria-label={`Follow-up attempts: ${entry.attempts} of ${entry.maxAttempts}`}
                          aria-valuenow={entry.attempts}
                          aria-valuemin={0}
                          aria-valuemax={entry.maxAttempts}
                        >
                          <div
                            className="h-full bg-primary rounded-full transition-colors"
                            style={{ width: `${(entry.attempts / entry.maxAttempts) * 100}%` }}
                          />
                        </div>
                      </div>
                    </TableCell>

                    {/* Summary */}
                    <TableCell>
                      {entry.aiSummary ? (
                        <span
                          className="text-sm text-muted-foreground"
                          title={entry.aiSummary}
                        >
                          {entry.aiSummary.length > 60
                            ? entry.aiSummary.slice(0, 60) + '...'
                            : entry.aiSummary}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">--</span>
                      )}
                    </TableCell>

                    {/* Notes */}
                    <TableCell>
                      {entry.notes ? (
                        <span
                          className="text-sm italic text-muted-foreground truncate max-w-[120px] block"
                          title={entry.notes}
                        >
                          {entry.notes}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">--</span>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleCallNow(entry)}
                            disabled={callingNowId === entry.id || entry.status === 'completed' || entry.status === 'cancelled'}
                          >
                            {callingNowId === entry.id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <PhoneCall className="mr-2 h-4 w-4" />
                            )}
                            Call Now
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => openRescheduleDialog(entry)}
                            disabled={entry.status === 'completed' || entry.status === 'cancelled'}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            Reschedule
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openNotesDialog(entry)}>
                            <MessageSquare className="mr-2 h-4 w-4" />
                            {entry.notes ? 'Edit Note' : 'Add Note'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleCancel(entry)}
                            disabled={cancellingId === entry.id || entry.status === 'completed' || entry.status === 'cancelled'}
                            className="text-destructive focus:text-destructive"
                          >
                            {cancellingId === entry.id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <XCircle className="mr-2 h-4 w-4" />
                            )}
                            Cancel
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Pagination */}
      {filteredEntries.length >= limit && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Showing {offset + 1}–{offset + filteredEntries.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + limit)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Reschedule Dialog */}
      <Dialog open={!!rescheduleEntry} onOpenChange={(open) => { if (!open) setRescheduleEntry(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule Follow-Up</DialogTitle>
            <DialogDescription>
              Choose a new date and time for the follow-up call to{' '}
              {rescheduleEntry?.callerName || formatPhoneDisplay(rescheduleEntry?.phoneNumber || '', 'this prospect')}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="reschedule-date">Date</Label>
              <Input
                id="reschedule-date"
                type="date"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="reschedule-time">Time</Label>
              <Input
                id="reschedule-time"
                type="time"
                value={rescheduleTime}
                onChange={(e) => setRescheduleTime(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleEntry(null)}>
              Cancel
            </Button>
            <Button onClick={handleReschedule} disabled={isRescheduling || !rescheduleDate || !rescheduleTime}>
              {isRescheduling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={!!notesEntry} onOpenChange={(open) => { if (!open) setNotesEntry(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{notesEntry?.notes ? 'Edit Note' : 'Add Note'}</DialogTitle>
            <DialogDescription>
              Add a note to the follow-up for{' '}
              {notesEntry?.callerName || formatPhoneDisplay(notesEntry?.phoneNumber || '', 'this prospect')}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="notes-text">Notes</Label>
              <Textarea
                id="notes-text"
                placeholder="Enter notes about this follow-up..."
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesEntry(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNotes} disabled={isSavingNotes}>
              {isSavingNotes ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
